import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    ArrowRight,
    BookOpen,
    Headphones,
    Sparkles,
    ShieldCheck,
    Infinity as InfinityIcon,
    Download,
    LifeBuoy,
} from "lucide-react";

const FALLBACK = {
    home_hero_eyebrow: "Apprendre · Comprendre · Pratiquer",
    home_hero_title: "Apprends l'islam\n**et développe tes compétences.**",
    home_hero_description: "« Et n'oublie pas ta part en cette vie d'ici-bas. »\n— Coran, sourate Al-Qasas (28:77)",
    home_hero_caption: "Une bibliothèque entière, pensée pour votre apprentissage et votre évolution.",
};

// Render a title with **gold** segments.
function renderTitle(raw) {
    if (!raw) return null;
    const lines = raw.split(/\r?\n/);
    return lines.map((line, li) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
            <span key={li}>
                {parts.map((p, i) => {
                    const m = /^\*\*([^*]+)\*\*$/.exec(p);
                    return m ? (
                        <span key={i} className="gold-text">{m[1]}</span>
                    ) : (
                        <span key={i}>{p}</span>
                    );
                })}
                {li < lines.length - 1 && <br />}
            </span>
        );
    });
}

// Decorative ornament — golden line with star, like the screenshot.
function Ornament() {
    return (
        <div className="flex items-center justify-center gap-2 my-2" aria-hidden="true">
            <span className="block h-px w-16" style={{ background: "linear-gradient(90deg, transparent, var(--gold-500))" }} />
            <svg viewBox="0 0 16 16" className="w-2.5 h-2.5" style={{ color: "var(--gold-400)" }} fill="currentColor">
                <path d="M8 0 L9.4 6.6 L16 8 L9.4 9.4 L8 16 L6.6 9.4 L0 8 L6.6 6.6 Z" />
            </svg>
            <span className="block h-px w-16" style={{ background: "linear-gradient(90deg, var(--gold-500), transparent)" }} />
        </div>
    );
}

// One book on the shelf. Uses a fixed aspect ratio + cover image as bg.
function ShelfBook({ product, idx }) {
    const cover = product.image_url;
    const fallbackGradient = useMemo(() => {
        const palettes = [
            "linear-gradient(135deg, #2D1810 0%, #5C2E1F 100%)",
            "linear-gradient(135deg, #1E2A4A 0%, #3D4E7A 100%)",
            "linear-gradient(135deg, #3E2723 0%, #6D4C41 100%)",
            "linear-gradient(135deg, #2C3E50 0%, #4A6280 100%)",
            "linear-gradient(135deg, #4E342E 0%, #795548 100%)",
        ];
        return palettes[idx % palettes.length];
    }, [idx]);

    return (
        <Link
            to={`/produit/${product.id}`}
            className="shelf-book group"
            data-testid={`shelf-book-${product.id}`}
            title={product.title}
        >
            <div
                className="shelf-book-cover"
                style={cover ? { backgroundImage: `url(${cover})` } : { background: fallbackGradient }}
            >
                {!cover && (
                    <div className="absolute inset-0 flex items-center justify-center p-2 text-center">
                        <span className="font-display text-[10px] leading-tight" style={{ color: "rgba(255,236,200,0.92)" }}>
                            {product.title}
                        </span>
                    </div>
                )}
                {/* Subtle gloss */}
                <span className="shelf-book-gloss" />
                {/* Spine */}
                <span className="shelf-book-spine" />
            </div>
            <div className="shelf-book-title" title={product.title}>{product.title}</div>
        </Link>
    );
}

function Bookshelf({ products }) {
    // 3 shelves × 6 books = 18 slots. We cycle through available products to fill
    // each shelf with real, clickable covers — never leave empty placeholders.
    const PER_SHELF = 6;
    const SHELVES = 3;
    const TOTAL = PER_SHELF * SHELVES;

    if (!products || products.length === 0) {
        return (
            <div
                className="rounded-2xl border flex items-center justify-center"
                style={{ minHeight: 340, borderColor: "var(--border-dark)", background: "rgba(20,16,10,0.6)" }}
            >
                <div className="text-sm" style={{ color: "var(--text-on-dark-soft)" }}>
                    La bibliothèque se remplit bientôt…
                </div>
            </div>
        );
    }

    const slots = Array.from({ length: TOTAL }, (_, i) => products[i % products.length]);
    const rows = [];
    for (let s = 0; s < SHELVES; s++) {
        rows.push(slots.slice(s * PER_SHELF, (s + 1) * PER_SHELF));
    }

    return (
        <div className="bookshelf" data-testid="home-bookshelf">
            {rows.map((row, ri) => (
                <div key={ri} className="bookshelf-row">
                    <div className="bookshelf-books">
                        {row.map((p, i) => (
                            <ShelfBook key={`${ri}-${i}-${p.id}`} product={p} idx={ri * PER_SHELF + i} />
                        ))}
                    </div>
                    <div className="bookshelf-plank" />
                </div>
            ))}
        </div>
    );
}

export default function Home() {
    const { user } = useAuth();
    const [shops, setShops] = useState([]);
    const [hero, setHero] = useState(FALLBACK);
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/shops").then((r) => setShops(r.data)).catch(() => {});
        api.get("/settings/public").then((r) => {
            setHero({
                home_hero_eyebrow: r.data.home_hero_eyebrow || FALLBACK.home_hero_eyebrow,
                home_hero_title: r.data.home_hero_title || FALLBACK.home_hero_title,
                home_hero_description: r.data.home_hero_description || FALLBACK.home_hero_description,
                home_hero_caption: r.data.home_hero_caption || FALLBACK.home_hero_caption,
            });
        }).catch(() => {});
        // Featured catalog for the shelves (public, all product types)
        api.get("/featured-products?limit=16").then((r) => {
            const items = (r.data || []).filter((p) => p && p.title);
            setFeatured(items.slice(0, 16));
        }).catch(() => {});
    }, []);

    const ctaTo = user ? "/library" : "/login";

    return (
        <div className="App dark-section" data-testid="page-home">
            {/* DARK HERO BAND */}
            <div className="dark-section relative">
                <Header variant="dark" />

                <section className="relative overflow-hidden lg:min-h-[calc(100vh-80px)] lg:flex lg:flex-col">
                    {/* HADITH BAND — full-width banner at the very top, above the bookshelf */}
                    <div
                        className="w-full pt-4 pb-2 lg:pt-5 lg:pb-3 px-4 sm:px-6 lg:px-12 xl:px-20"
                        style={{ borderBottom: "1px solid rgba(201, 164, 88, 0.18)" }}
                        data-testid="hero-hadith"
                    >
                        <div className="max-w-[1500px] mx-auto text-center">
                            <div className="text-[10px] font-semibold tracking-[0.32em] uppercase mb-2" style={{ color: "var(--gold-300)" }}>
                                Hadith du Prophète ﷺ
                            </div>
                            <blockquote
                                className="font-display italic text-[20px] sm:text-[26px] lg:text-[28px] xl:text-[32px] leading-[1.18] max-w-5xl mx-auto"
                                style={{ color: "var(--text-on-dark)" }}
                            >
                                « Quérir la science de la religion est une obligation pour tout musulman. »
                            </blockquote>
                            <div className="text-[11px] mt-1.5 tracking-wider" style={{ color: "var(--gold-300)" }}>
                                — Rapporté par Ibn Mâjah
                            </div>
                        </div>
                    </div>

                    <div
                        className="w-full grid grid-cols-1 lg:grid-cols-12 gap-6 items-start pt-4 pb-3 lg:flex-1 pl-4 sm:pl-6 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))] pr-4 sm:pr-6 lg:pr-0"
                    >
                        <div className="lg:col-span-5 fade-up lg:max-w-[600px]">
                            <div className="text-[11px] font-semibold tracking-[0.28em] uppercase mb-4" style={{ color: "var(--gold-300)" }}>
                                {hero.home_hero_eyebrow}
                            </div>

                            <h1
                                className="font-display text-4xl sm:text-5xl lg:text-[44px] xl:text-[52px] leading-[1.05] tracking-tight"
                                style={{ color: "var(--text-on-dark)" }}
                                data-testid="hero-title"
                            >
                                {renderTitle(hero.home_hero_title)}
                            </h1>

                            {/* VERSET avec crochets coraniques inline */}
                            <div className="mt-5 mb-5" data-testid="hero-description">
                                <blockquote
                                    className="font-display italic text-[20px] sm:text-[22px] lg:text-[23px] leading-[1.35] max-w-xl"
                                    style={{ color: "var(--text-on-dark)" }}
                                >
                                    <span className="quran-bracket">﴾</span>
                                    &nbsp;Et n'oublie pas ta part de cette vie d'ici-bas.&nbsp;
                                    <span className="quran-bracket">﴿</span>
                                </blockquote>
                                <div className="text-xs mt-2 tracking-wider" style={{ color: "var(--gold-400)" }}>
                                    — Coran, sourate Al-Qasas (28:77)
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <Link to="/catalogue" className="btn-gold" data-testid="hero-cta-discover">
                                    <BookOpen className="w-4 h-4" /> Découvrir les ressources <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to={ctaTo} className="btn-light" data-testid="hero-cta-login">
                                    {user ? "Mon espace" : "Se connecter"}
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-7 fade-in lg:pr-6">
                            <Bookshelf products={featured} />
                            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-4 lg:pr-0">
                                <div className="text-xs inline-flex items-start gap-2" style={{ color: "var(--text-on-dark-soft)" }}>
                                    <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--gold-300)" }} />
                                    <span>{hero.home_hero_caption}</span>
                                </div>
                                <Link to="/ebooks" className="bookshelf-cta" data-testid="hero-shelf-all">
                                    <BookOpen className="w-4 h-4" /> Voir toute la bibliothèque <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* FEATURE BAND — full-width, prominent, at bottom of hero */}
                    <div
                        className="w-full mt-auto pt-6 pb-7 lg:pt-7 lg:pb-8 px-4 sm:px-6 lg:px-12 xl:px-20"
                        style={{ borderTop: "1px solid rgba(201, 164, 88, 0.18)" }}
                        data-testid="hero-features"
                    >
                        <div className="max-w-[1500px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6">
                            {[
                                { icon: ShieldCheck, title: "Paiement sécurisé", desc: "100% sécurisé avec Stripe" },
                                { icon: InfinityIcon, title: "Accès illimité", desc: "Tous les contenus inclus tant que votre abonnement est actif" },
                                { icon: Download, title: "Lecture en ligne", desc: "PDF, ebooks et audios accessibles partout" },
                                { icon: LifeBuoy, title: "Support dédié", desc: "Une équipe disponible pour vous accompagner" },
                            ].map(({ icon: Icon, title, desc }) => (
                                <div key={title} className="flex items-start gap-4">
                                    <div
                                        className="flex-shrink-0 rounded-full flex items-center justify-center"
                                        style={{
                                            width: 52,
                                            height: 52,
                                            background: "linear-gradient(180deg, rgba(242,221,164,0.22) 0%, rgba(201,164,88,0.14) 100%)",
                                            color: "var(--gold-300)",
                                            border: "1px solid rgba(201, 164, 88, 0.40)",
                                            boxShadow: "inset 0 1px 0 rgba(255, 236, 200, 0.22), 0 8px 20px -10px rgba(201, 164, 88, 0.45)",
                                        }}
                                    >
                                        <Icon className="w-6 h-6" strokeWidth={1.6} />
                                    </div>
                                    <div className="min-w-0 pt-0.5">
                                        <div
                                            className="font-display text-[18px] leading-tight"
                                            style={{ color: "var(--text-on-dark)" }}
                                        >
                                            {title}
                                        </div>
                                        <div
                                            className="text-[13px] leading-snug mt-1"
                                            style={{ color: "var(--text-on-dark-soft)" }}
                                        >
                                            {desc}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* SHOPS / UNIVERS section */}
            <section className="pt-24 pb-20" style={{ background: "var(--bg-main)" }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="eyebrow mb-3">Nos univers</div>
                        <h2 className="font-display text-4xl sm:text-5xl leading-tight">Deux espaces, deux signatures.</h2>
                        <p className="mt-4 text-base" style={{ color: "var(--text-secondary)" }}>
                            Chaque univers propose son propre catalogue, sélectionné avec exigence.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="home-shops-grid">
                        {shops.map((s) => (
                            <Link
                                key={s.id}
                                to={`/boutique/${s.slug}`}
                                className="card-editorial rounded-xl overflow-hidden flex flex-col group"
                                data-testid={`shop-card-${s.slug}`}
                            >
                                <div className="aspect-[16/9] overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
                                    {s.avatar_url && (
                                        <img src={s.avatar_url} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    )}
                                </div>
                                <div className="p-7 flex-1 flex flex-col">
                                    <div className="eyebrow mb-2">Univers exclusif</div>
                                    <h3 className="font-display text-3xl leading-tight mb-3">{s.name}</h3>
                                    <p className="text-sm leading-relaxed flex-1" style={{ color: "var(--text-secondary)" }}>
                                        {s.tagline}
                                    </p>
                                    <div
                                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold"
                                        style={{ color: "var(--gold-600)" }}
                                    >
                                        Visiter l'univers <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* QUALITY MANIFESTO */}
            <section className="border-t" style={{ borderColor: "var(--border-light)", background: "var(--bg-secondary)" }}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
                    <Headphones className="w-7 h-7 mx-auto mb-5" style={{ color: "var(--gold-500)" }} strokeWidth={1.5} />
                    <div className="eyebrow mb-3">L'exigence comme ligne directrice</div>
                    <h2 className="font-display text-4xl sm:text-5xl leading-tight">
                        Du <span className="gold-text">contenu pensé</span> pour durer.
                    </h2>
                    <p className="mt-6 text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
                        Chaque ressource publiée sur Savoir Utile est sélectionnée pour sa rigueur, sa clarté et sa profondeur. Pas de bruit, pas d'inutile — uniquement des contenus à valeur durable.
                    </p>
                </div>
            </section>

            {/* CTA FINAL */}
            <section className="dark-section">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
                    <Sparkles className="w-7 h-7 mx-auto mb-5" style={{ color: "var(--gold-300)" }} strokeWidth={1.5} />
                    <h2 className="font-display text-4xl sm:text-5xl mb-4" style={{ color: "var(--text-on-dark)" }}>
                        Votre <span className="gold-text">espace personnel</span>.
                    </h2>
                    <p className="text-base max-w-xl mx-auto mb-8" style={{ color: "var(--text-on-dark-soft)" }}>
                        Connectez-vous pour retrouver votre bibliothèque, reprendre vos lectures et écouter vos cours.
                    </p>
                    <Link to={ctaTo} className="btn-gold" data-testid="cta-final-space">
                        Accéder à mon espace <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
